// ==========================================
// MASTER ONTOLOGY
// ==========================================
// This file aggregates all domain modules into a single ontology.
// Runtime validation ensures no duplicate IDs exist.

import { MasterOntology, Procedure, Representation, Tag } from '../ontology.model';

// Import primitives (reusable base representations)
import { primitiveRepresentations } from './primitives';

// Import tags from each folder level
import {
  partialFractionsProcedures,
  partialFractionsRepresentations,
  partialFractionsTags,
} from './algebra/partial-fractions';
import { arithmeticProcedures, arithmeticRepresentations, arithmeticTags } from './algebra/sequences/arithmetic';
import { geometricProcedures, geometricRepresentations, geometricTags } from './algebra/sequences/geometric';
import { sequencesTags } from './algebra/sequences/tags';
import { algebraTags } from './algebra/tags';

import { standardFormProcedures, standardFormRepresentations, standardFormTags } from './number/standard-form';
import { numberTags } from './number/tags';

// ==========================================
// AGGREGATE ALL DATA
// ==========================================

const allTags: Tag[] = [
  ...algebraTags,
  ...sequencesTags,
  ...arithmeticTags,
  ...geometricTags,
  ...partialFractionsTags,
  ...numberTags,
  ...standardFormTags,
];

const allRepresentations: Representation[] = [
  ...primitiveRepresentations,
  ...arithmeticRepresentations,
  ...geometricRepresentations,
  ...partialFractionsRepresentations,
  ...standardFormRepresentations,
];

const allProcedures: Procedure[] = [
  ...arithmeticProcedures,
  ...geometricProcedures,
  ...partialFractionsProcedures,
  ...standardFormProcedures,
];

// ==========================================
// UNIQUENESS VALIDATION (AI GUARDRAILS)
// ==========================================
// These functions provide deterministic guardrails for AI code generation.
// When a duplicate ID is detected, the error message includes ALL existing IDs
// so the AI can generate a unique alternative.

interface ValidationError {
  type: 'DUPLICATE_ID';
  entityType: string;
  duplicateId: string;
  existingIds: string[];
  suggestion: string;
}

function validateUniqueIds<T extends { id: string; name?: string }>(items: T[], entityType: string): void {
  const idToItems = new Map<string, T[]>();

  for (const item of items) {
    const existing = idToItems.get(item.id) ?? [];
    existing.push(item);
    idToItems.set(item.id, existing);
  }

  const duplicates = Array.from(idToItems.entries()).filter(([, dupes]) => dupes.length > 1);

  if (duplicates.length > 0) {
    const allIds = Array.from(idToItems.keys()).sort();
    const duplicateDetails = duplicates
      .map(([id, dupes]) => {
        const names = dupes.map((i) => i.name ?? 'unnamed').join(', ');
        return `  - "${id}" (used by: ${names})`;
      })
      .join('\n');

    const error: ValidationError = {
      type: 'DUPLICATE_ID',
      entityType,
      duplicateId: duplicates[0][0],
      existingIds: allIds,
      suggestion: `Generate a new unique ID that is NOT in the existing list`,
    };

    throw new Error(
      `[ONTOLOGY_VALIDATION_ERROR]\n` +
        `Type: DUPLICATE_${entityType.toUpperCase()}_ID\n` +
        `\n` +
        `Duplicate ${entityType} IDs detected:\n${duplicateDetails}\n` +
        `\n` +
        `EXISTING_${entityType.toUpperCase()}_IDS (${allIds.length} total):\n` +
        `${allIds.map((id) => `  - ${id}`).join('\n')}\n` +
        `\n` +
        `ACTION_REQUIRED: Change the duplicate ID to something unique.\n` +
        `The new ID must:\n` +
        `  1. Follow pattern: ${getPatternForEntity(entityType)}\n` +
        `  2. NOT be in the EXISTING_${entityType.toUpperCase()}_IDS list above\n` +
        `  3. Be descriptive of what the entity represents\n` +
        `\n` +
        `JSON: ${JSON.stringify(error)}`,
    );
  }
}

function getPatternForEntity(entityType: string): string {
  switch (entityType) {
    case 'Tag':
      return 'tag.{descriptive_name}';
    case 'Representation':
      return 'rep.{unique_descriptive_name}';
    case 'Procedure':
      return 'proc.{unique_action_name}';
    default:
      return `${entityType.toLowerCase()}.{unique_name}`;
  }
}

// ==========================================
// REFERENCE VALIDATION (AI GUARDRAILS)
// ==========================================
// Ensures all procedure input_ids and output_id reference existing representations.

interface ReferenceError {
  type: 'INVALID_REFERENCE';
  procedureId: string;
  procedureName: string;
  invalidIds: string[];
  referenceType: 'input' | 'output';
  existingRepresentationIds: string[];
}

function validateProcedureReferences(procs: Procedure[], reps: Representation[]): void {
  const repIds = new Set(reps.map((r) => r.id));
  const errors: ReferenceError[] = [];

  for (const proc of procs) {
    // Check input_ids
    const invalidInputs = proc.input_ids.filter((id) => !repIds.has(id));
    if (invalidInputs.length > 0) {
      errors.push({
        type: 'INVALID_REFERENCE',
        procedureId: proc.id,
        procedureName: proc.name,
        invalidIds: invalidInputs,
        referenceType: 'input',
        existingRepresentationIds: Array.from(repIds).sort(),
      });
    }

    // Check output_id
    if (!repIds.has(proc.output_id)) {
      errors.push({
        type: 'INVALID_REFERENCE',
        procedureId: proc.id,
        procedureName: proc.name,
        invalidIds: [proc.output_id],
        referenceType: 'output',
        existingRepresentationIds: Array.from(repIds).sort(),
      });
    }
  }

  if (errors.length > 0) {
    const existingRepIds = Array.from(repIds).sort();
    const errorDetails = errors
      .map((e) => {
        return (
          `  - Procedure "${e.procedureId}" (${e.procedureName}):\n` +
          `    ${e.referenceType}_id(s) not found: ${e.invalidIds.join(', ')}`
        );
      })
      .join('\n');

    throw new Error(
      `[ONTOLOGY_VALIDATION_ERROR]\n` +
        `Type: INVALID_PROCEDURE_REFERENCE\n` +
        `\n` +
        `Procedures reference non-existent Representation IDs:\n${errorDetails}\n` +
        `\n` +
        `EXISTING_REPRESENTATION_IDS (${existingRepIds.length} total):\n` +
        `${existingRepIds.map((id) => `  - ${id}`).join('\n')}\n` +
        `\n` +
        `ACTION_REQUIRED: Fix the procedure's input_ids/output_id.\n` +
        `Each referenced ID must:\n` +
        `  1. Exist in the EXISTING_REPRESENTATION_IDS list above\n` +
        `  2. Be a valid representation that provides the required data shape\n` +
        `\n` +
        `If the representation doesn't exist yet, create it first.\n` +
        `\n` +
        `JSON: ${JSON.stringify(errors[0])}`,
    );
  }
}

// ==========================================
// TAG REFERENCE VALIDATION (AI GUARDRAILS)
// ==========================================
// Ensures all tag_ids in representations and procedures reference existing tags.

interface TagReferenceError {
  type: 'INVALID_TAG_REFERENCE';
  entityType: 'Representation' | 'Procedure';
  entityId: string;
  entityName: string;
  invalidTagIds: string[];
}

function validateTagReferences(tags: Tag[], reps: Representation[], procs: Procedure[]): void {
  const tagIds = new Set(tags.map((t) => t.id));
  const errors: TagReferenceError[] = [];

  // Check representations
  for (const rep of reps) {
    const invalidTags = rep.tag_ids.filter((id) => !tagIds.has(id));
    if (invalidTags.length > 0) {
      errors.push({
        type: 'INVALID_TAG_REFERENCE',
        entityType: 'Representation',
        entityId: rep.id,
        entityName: rep.name,
        invalidTagIds: invalidTags,
      });
    }
  }

  // Check procedures (if they have tag_ids)
  for (const proc of procs) {
    if (proc.tag_ids && proc.tag_ids.length > 0) {
      const invalidTags = proc.tag_ids.filter((id) => !tagIds.has(id));
      if (invalidTags.length > 0) {
        errors.push({
          type: 'INVALID_TAG_REFERENCE',
          entityType: 'Procedure',
          entityId: proc.id,
          entityName: proc.name,
          invalidTagIds: invalidTags,
        });
      }
    }
  }

  if (errors.length > 0) {
    const existingTagIds = Array.from(tagIds).sort();
    const errorDetails = errors
      .map((e) => {
        return (
          `  - ${e.entityType} "${e.entityId}" (${e.entityName}):\n` +
          `    tag_ids not found: ${e.invalidTagIds.join(', ')}`
        );
      })
      .join('\n');

    throw new Error(
      `[ONTOLOGY_VALIDATION_ERROR]\n` +
        `Type: INVALID_TAG_REFERENCE\n` +
        `\n` +
        `Entities reference non-existent Tag IDs:\n${errorDetails}\n` +
        `\n` +
        `EXISTING_TAG_IDS (${existingTagIds.length} total):\n` +
        `${existingTagIds.map((id) => `  - ${id}`).join('\n')}\n` +
        `\n` +
        `ACTION_REQUIRED: Fix the entity's tag_ids.\n` +
        `Each referenced tag ID must:\n` +
        `  1. Exist in the EXISTING_TAG_IDS list above\n` +
        `  2. Be a valid tag that categorizes this entity\n` +
        `\n` +
        `If the tag doesn't exist yet, create it first in the appropriate tags.ts file.\n` +
        `\n` +
        `JSON: ${JSON.stringify(errors[0])}`,
    );
  }
}

// ==========================================
// OPTIONAL FIELD VALIDATION (AI GUARDRAILS)
// ==========================================
// Warns when a procedure's input representations have optional fields,
// since the execute function might not handle undefined values properly.

interface OptionalFieldWarning {
  type: 'OPTIONAL_FIELD_IN_INPUT';
  procedureId: string;
  procedureName: string;
  representationId: string;
  representationName: string;
  optionalFields: string[];
}

function validateProcedureInputFields(procs: Procedure[], reps: Representation[]): void {
  const repMap = new Map(reps.map((r) => [r.id, r]));
  const warnings: OptionalFieldWarning[] = [];

  for (const proc of procs) {
    for (const inputId of proc.input_ids) {
      const rep = repMap.get(inputId);
      if (!rep) {
        continue;
      }

      const optionalFields: string[] = [];
      for (const [fieldName, fieldDef] of Object.entries(rep.model)) {
        if (fieldDef && typeof fieldDef === 'object' && 'optional' in fieldDef && fieldDef.optional === true) {
          optionalFields.push(fieldName);
        }
      }

      if (optionalFields.length > 0) {
        warnings.push({
          type: 'OPTIONAL_FIELD_IN_INPUT',
          procedureId: proc.id,
          procedureName: proc.name,
          representationId: rep.id,
          representationName: rep.name,
          optionalFields,
        });
      }
    }
  }

  if (warnings.length > 0) {
    const warningDetails = warnings
      .map((w) => {
        return (
          `  - Procedure "${w.procedureId}" (${w.procedureName}):\n` +
          `    Input "${w.representationId}" (${w.representationName}) has optional fields: ${w.optionalFields.join(
            ', ',
          )}\n` +
          `    ⚠️  Ensure the execute function handles undefined values for these fields.`
        );
      })
      .join('\n');

    console.warn(
      `[ONTOLOGY_WARNING]\n` +
        `Type: OPTIONAL_FIELDS_IN_PROCEDURE_INPUTS\n` +
        `\n` +
        `The following procedures use input representations with optional fields:\n${warningDetails}\n` +
        `\n` +
        `RECOMMENDATION: Procedure execute functions should handle undefined values,\n` +
        `or consider making these fields required in the representation model.\n`,
    );
  }
}

// Run validation at module load time (catches errors during development/build)
validateUniqueIds(allTags, 'Tag');
validateUniqueIds(allRepresentations, 'Representation');
validateUniqueIds(allProcedures, 'Procedure');
validateProcedureReferences(allProcedures, allRepresentations);
validateTagReferences(allTags, allRepresentations, allProcedures);
validateProcedureInputFields(allProcedures, allRepresentations);

// ==========================================
// AI UTILITIES
// ==========================================
// These exported functions allow AI agents to check ID availability
// BEFORE attempting to create entities, avoiding validation errors.

/** Check if an ID is available before using it */
export function isIdAvailable(id: string, entityType: 'tag' | 'representation' | 'procedure'): boolean {
  switch (entityType) {
    case 'tag':
      return !allTags.some((t) => t.id === id);
    case 'representation':
      return !allRepresentations.some((r) => r.id === id);
    case 'procedure':
      return !allProcedures.some((p) => p.id === id);
  }
}

/** Get all existing IDs for a given entity type (for AI to avoid collisions) */
export function getExistingIds(entityType: 'tag' | 'representation' | 'procedure'): string[] {
  switch (entityType) {
    case 'tag':
      return allTags.map((t) => t.id);
    case 'representation':
      return allRepresentations.map((r) => r.id);
    case 'procedure':
      return allProcedures.map((p) => p.id);
  }
}

/** Check if a representation ID exists (for validating procedure references) */
export function representationExists(repId: string): boolean {
  return allRepresentations.some((r) => r.id === repId);
}

/** Validate that all representation IDs in a list exist */
export function validateRepresentationIds(repIds: string[]): { valid: boolean; missing: string[] } {
  const existingIds = new Set<string>(allRepresentations.map((r) => r.id));
  const missing = repIds.filter((id) => !existingIds.has(id));
  return { valid: missing.length === 0, missing };
}

/** Check if a tag ID exists (for validating tag references in representations/procedures) */
export function tagExists(tagId: string): boolean {
  return allTags.some((t) => t.id === tagId);
}

/** Validate that all tag IDs in a list exist */
export function validateTagIds(tagIds: string[]): { valid: boolean; missing: string[] } {
  const existingIds = new Set<string>(allTags.map((t) => t.id));
  const missing = tagIds.filter((id) => !existingIds.has(id));
  return { valid: missing.length === 0, missing };
}

// ==========================================
// EXPORT
// ==========================================

export const masterOntology: MasterOntology = {
  generated_at: new Date().toISOString(),
  version: '3.2.0',
  tags: allTags,
  representations: allRepresentations,
  procedures: allProcedures,
};

// Also export individual arrays for convenience
export const tags = allTags;
export const representations = allRepresentations;
export const procedures = allProcedures;

// Re-export domain modules
export * from './algebra/partial-fractions';
export * from './algebra/sequences/arithmetic';
export * from './algebra/sequences/geometric';
export * from './number/standard-form';
export * from './primitives';
